import {
  type NumberSettingEvent,
  useNumberSettingPresenter,
} from "@/features/settings/ui/useNumberSettingPresenter";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";
import { Input, type InputProps } from "@/shared/ui/form";

export interface NumberSettingInputProps
  extends Omit<
    InputProps,
    "value" | "onChange" | "type" | "defaultValue" | "min" | "max"
  > {
  /** 現在確定している数値(ストア値)。 */
  value: number;
  /** blur / Enter の確定時に呼ぶ。渡す値は [min, max] へ丸め済み。 */
  onCommit: (value: number) => void;
  /** 下限。省略時は下限なし。 */
  min?: number;
  /** 上限。省略時は上限なし。 */
  max?: number;
}

/**
 * 数値設定用の入力欄。
 *
 * type="number" の onChange で毎キー clamp 済み setter を呼ぶと、目的の値の
 * 途中経過(例: "1000" の1桁目 "1")が範囲外になった瞬間に下限へ丸められ、
 * 目的の値をキーボードで入力できない。これを避けるため、編集中はローカルの
 * draft 文字列で自由入力を許し、blur と Enter の確定時にのみ [min, max] へ
 * 丸めて onCommit する。ストア値(value)が外部から変わった場合は、編集中で
 * なければ draft を同期する(全データクリア等の外部更新に追従する)。
 */
export const NumberSettingInput: React.FunctionComponent<
  NumberSettingInputProps
> = ({ value, onCommit, ...props }: NumberSettingInputProps) => {
  const { node, draft } = useNumberSettingPresenter(
    value,
    onCommit,
    props.min,
    props.max,
  );
  return (
    <ComponentScope node={node}>
      <NumberSettingInputView
        {...props}
        draft={draft}
        onEvent={(event) => {
          node.dispatch(event);
        }}
      />
    </ComponentScope>
  );
};
type NumberSettingInputViewProps = Omit<
  NumberSettingInputProps,
  "value" | "onCommit"
> & { draft: string; onEvent: (event: NumberSettingEvent) => void };
export function NumberSettingInputView({
  draft,
  onEvent,
  min,
  max,
  onFocus,
  onBlur,
  onKeyDown,
  ...rest
}: NumberSettingInputViewProps): React.ReactNode {
  return (
    <Input
      {...rest}
      type="number"
      min={min}
      max={max}
      value={draft}
      onFocus={(event: React.FocusEvent<HTMLInputElement>): void => {
        onEvent({ type: "number-setting.focus" });
        onFocus?.(event);
      }}
      onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
        onEvent({ type: "number-setting.input", value: event.target.value });
      }}
      onBlur={(event: React.FocusEvent<HTMLInputElement>): void => {
        onEvent({ type: "number-setting.commit", blur: true });
        onBlur?.(event);
      }}
      onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>): void => {
        if (event.key === "Enter") {
          // 確定中はまだ blur していないため、丸めた値を draft へ即時反映する。
          onEvent({ type: "number-setting.commit", blur: false });
        }
        onKeyDown?.(event);
      }}
    />
  );
}

NumberSettingInput.displayName = "NumberSettingInput";
